import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../../app.config';
import { StateService } from '../state.service';
import { Bundle, ChargeItem, Invoice, ServiceRequest } from 'fhir/r4';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { referenceArrayContains, referenceMatches } from '../fhir-reference.util';
import { LabBillingPlaceholderService } from './lab-billing-placeholder.service';
import { inferServiceRequestType } from '../service-request-type.util';
import { UtilityService } from '../utility.service';

/**
 * NOTE: This service is intentionally a COPY of the lab billing placeholder patterns,
 * adapted for general ServiceRequest categories. We keep it separate so lab flows
 * remain stable and unchanged.
 */
@Injectable({
  providedIn: 'root'
})
export class ServiceRequestBillingPlaceholderService {
  private readonly http = inject(HttpClient);
  private readonly backendEndPoint = inject(backendEndPointToken);
  private readonly stateService = inject(StateService);
  utilityService = inject(UtilityService);
  private readonly labBilling = inject(LabBillingPlaceholderService);

  /**
   * Generic payment resolver:
   * - Find ChargeItem(s) whose `service[]` contains `ServiceRequest/{id}`
   * - Confirm at least one BALANCED Invoice references one of those ChargeItems (lineItem.chargeItemReference)
   */
  resolvePaymentForServiceRequest(
    serviceRequest: ServiceRequest,
    patientId: string
  ): Observable<{ chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }> {
    const srId = (serviceRequest?.id ?? '').toString();
    if (!srId) return of({ chargeItems: [], invoices: [], hasBalancedInvoice: false });
    const srRef = `ServiceRequest/${srId}`;

    const processBundle = (bundle: Bundle<any>) => {
      const currentPatientId = this.stateService.currentPatientId$.getValue();
      const isCurrentPatient = !!currentPatientId && currentPatientId === patientId;
      if (isCurrentPatient) {
        this.stateService.processBundleTransaction(bundle as any);
      } else {
        this.stateService.processOrgWideBundleTransaction(bundle as any);
      }
    };

    const fromStateChargeItems = (this.stateService.PatientResources.chargeItems.getValue() || []).map((w) => w.actualResource);
    const fromStateInvoices = (this.stateService.PatientResources.invoices.getValue() || []).map((w) => w.actualResource);

    // const chargeItems$ = fromStateChargeItems.length
    //   ? of(fromStateChargeItems)
    //   : this.http
    //     .get<Bundle<ChargeItem>>(`${this.backendEndPoint}/ChargeItem?subject=${encodeURIComponent(`Patient/${patientId}`)}&_count=1000`)
    //     .pipe(
    //       tap((bundle) => processBundle(bundle as any)),
    //       map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as ChargeItem[]),
    //       catchError(() => of([] as ChargeItem[]))
    //     );
    // Use three-tier query (same pattern as lab-billing) to fetch charge items and sync to state
        const currentPatientId = this.stateService.currentPatientId$.getValue();
        const scope: 'patient' | 'orgWide' = currentPatientId && currentPatientId === patientId ? 'patient' : 'orgWide';
        const subjectRefOr = [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`];

        const chargeItems$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
          resourceType: 'ChargeItem',
          urlSearchObject: { subject: `Patient/${patientId}`,
        status: 'billable,billed' },
          querySearchObject: [
            { key: 'subject.reference', value: subjectRefOr },
            { key: 'status', value: ['billable', 'billed'] }
          ],
          patientId,
          scope,
          count: 1000,
          tier1FollowNextLinks: true,
          tier1MaxPages: 100
        });

    // We intentionally fetch invoices broadly (mirrors lab service) and filter locally.
    // const invoices$ = fromStateInvoices.length
    //   ? of(fromStateInvoices)
    //   : this.http
    //     .get<Bundle<Invoice>>(`${this.backendEndPoint}/Invoice`)
    //     .pipe(
    //       tap((bundle) => processBundle(bundle as any)),
    //       map((b: any) => (b?.entry || []).map((e: any) => e?.resource).filter(Boolean) as Invoice[]),
    //       catchError(() => of([] as Invoice[]))
    //     );
    // Use three-tier query for invoices (mirror lab-billing). Keep org-wide scope to avoid patient-only server restrictions
        const invoices$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
      resourceType: 'Invoice',
          urlSearchObject: { subject: `Patient/${patientId}` },
          querySearchObject: [
            { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`] }
          ],
          // prefer org-wide scope for invoices to match lab behavior
          scope: 'orgWide',
          patientId,
          count: 1000,
          tier1FollowNextLinks: true,
          tier1MaxPages: 100,
          expectedMinLength: 0,
          tier3TriggerWhenBelowExpectedMinOnTier1Failure: false
        });

    const generic$ = forkJoin({ chargeItems: chargeItems$, invoices: invoices$ }).pipe(
      map(({ chargeItems, invoices }) => {
        const linkedChargeItems = (chargeItems || []).filter((ci: any) => {
          const services = Array.isArray(ci?.service) ? ci.service : [];
          return services.some((s: any) => {
            const ref = (s?.reference ?? '').toString();
            if (!ref) return false;
            return ref === srRef || referenceMatches(ref, srRef);
          });
        }) as ChargeItem[];

        const chargeItemRefs = new Set(linkedChargeItems.filter((c: any) => !!c?.id).map((c: any) => `ChargeItem/${c.id}`));

        const hasBalancedInvoice = (invoices || []).some((inv: any) => {
          const status = (inv?.status || '').toString().toLowerCase();
          if (status !== 'balanced') return false;
          const lineItems = inv?.lineItem || [];
          return lineItems.some((li: any) => {
            const ref = (li?.chargeItemReference?.reference || '').toString();
            if (!ref) return false;
            // exact or suffix match
            for (const target of chargeItemRefs) {
              if (ref === target || ref.endsWith(target)) return true;
            }
            return false;
          });
        });

        // Return filtered invoices too (optional, for UI)
        const filteredInvoices = (invoices || []).filter((inv: any) => {
          const lineItems = inv?.lineItem || [];
          return lineItems.some((li: any) => {
            const ref = (li?.chargeItemReference?.reference || '').toString();
            if (!ref) return false;
            for (const target of chargeItemRefs) {
              if (ref === target || ref.endsWith(target)) return true;
            }
            return false;
          });
        }) as Invoice[];

        return { chargeItems: linkedChargeItems, invoices: filteredInvoices, hasBalancedInvoice };
      })
    );

    // Try lab-style placeholder resolution first (observations -> preferred chargeItem).
    // If lab resolver returns meaningful results, map and return them. Otherwise fall back
    // to the generic ChargeItem-based logic above.
    // Prefer type-specific lab billing resolution when available
    const srType = inferServiceRequestType(serviceRequest);
    try {
      if (srType === 'laboratory') {
        return this.labBilling.resolveBillingAndPlaceholderForLab(serviceRequest, patientId).pipe(
          map((labRes: any) => {
            const cis: ChargeItem[] = labRes.chargeItem ? [labRes.chargeItem] : (labRes.chargeItems || []);
            const invs: Invoice[] = labRes.linkedInvoices || labRes.invoices || [];
            return { chargeItems: cis, invoices: invs, hasBalancedInvoice: !!labRes.hasBalancedInvoice };
          }),
          catchError(() => generic$)
        );
      }

      if (srType === 'imaging') {
        return this.labBilling.resolveBillingAndPlaceholderForImagingStudy(serviceRequest, patientId).pipe(
          map((labRes: any) => {
            const cis: ChargeItem[] = labRes.chargeItem ? [labRes.chargeItem] : (labRes.chargeItems || []);
            const invs: Invoice[] = labRes.linkedInvoices || labRes.invoices || [];
            return { chargeItems: cis, invoices: invs, hasBalancedInvoice: !!labRes.hasBalancedInvoice };
          }),
          catchError(() => generic$)
        );
      }

      if (srType === 'procedure' || srType === 'surgery') {
        return this.labBilling.resolveBillingAndPlaceholderForProcedure(serviceRequest, patientId).pipe(
          map((labRes: any) => {
            const cis: ChargeItem[] = labRes.chargeItem ? [labRes.chargeItem] : (labRes.chargeItems || []);
            const invs: Invoice[] = labRes.linkedInvoices || labRes.invoices || [];
            return { chargeItems: cis, invoices: invs, hasBalancedInvoice: !!labRes.hasBalancedInvoice };
          }),
          catchError(() => generic$)
        );
      }

      // Fallback to generic chargeItem->ServiceRequest matching
        return this.labBilling.resolveBillingAndPlaceholderForLab(serviceRequest, patientId).pipe(
          map((labRes: any) => {
            const cis: ChargeItem[] = labRes.chargeItem ? [labRes.chargeItem] : (labRes.chargeItems || []);
            const invs: Invoice[] = labRes.linkedInvoices || labRes.invoices || [];
            return { chargeItems: cis, invoices: invs, hasBalancedInvoice: !!labRes.hasBalancedInvoice };
          }),
          catchError(() => generic$)
        );
    } catch (e) {
      return generic$;
    }
  }
  resolvePaymentForServiceRequests(
    serviceRequests: ServiceRequest[],
    patientId: string
  ): Observable<{ hasBalancedInvoiceForAll: boolean; byId: Record<string, boolean> }> {
    const list = (serviceRequests || []).filter(Boolean);
    if (!list.length) return of({ hasBalancedInvoiceForAll: true, byId: {} });

    return forkJoin(
      list.map((sr) =>
        this.resolvePaymentForServiceRequest(sr, patientId).pipe(
          map((r) => ({ id: (sr?.id ?? '').toString(), ok: r.hasBalancedInvoice }))
        )
      )
    ).pipe(
      map((results) => {
        const byId: Record<string, boolean> = {};
        for (const r of results) {
          if (!r.id) continue;
          byId[r.id] = r.ok;
        }
        return { hasBalancedInvoiceForAll: results.every((r) => r.ok), byId };
      })
    );
  }

  batchResolvePaymentForServiceRequests(
    serviceRequests: ServiceRequest[]
  ): Observable<Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }>> {
    const list = (serviceRequests || []).filter((sr) => !!sr.id);
    if (!list.length) return of({});

    const patientIds = new Set<string>();
    for (const sr of list) {
        const patientRef = sr.subject?.reference || '';
        const patientId = patientRef.split('/').pop() || '';
        if (patientId) {
            patientIds.add(patientId);
            patientIds.add(`${this.backendEndPoint}/Patient/${patientId}`);
        }
    }
    const patientIdList = Array.from(patientIds);

    const chargeItems$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
      resourceType: 'ChargeItem',
      urlSearchObject: {
        subject: { or: patientIdList },
        status: 'billable,billed'
      },
      querySearchObject: [
        { key: 'subject.reference', value: patientIdList.map((id) => id.startsWith('http') ? id : `Patient/${id}`) },
        { key: 'status', value: ['billable', 'billed'] }
      ],
      scope: 'orgWide',
      count: 10000,
      tier1FollowNextLinks: true,
      tier1MaxPages: 100,
      expectedMinLength: 0,
      tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
    });

    const invoices$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
      resourceType: 'Invoice',
      urlSearchObject: {
        status: 'balanced',
        subject: { or: patientIdList }
      },
      querySearchObject: [
        { key: 'status', value: 'balanced' },
        { key: 'subject.reference', value: patientIdList.map((id) => id.startsWith('http') ? id : `Patient/${id}`) }
      ],
      scope: 'orgWide',
      // patientId: patientIdList[0] || null,
      count: 10000,
      tier1FollowNextLinks: true,
      tier1MaxPages: 100,
      expectedMinLength: 0,
      tier3TriggerWhenBelowExpectedMinOnTier1Failure: true
    });


    // Also call lab-billing batch resolvers for SRs that are lab/imaging/procedure
    const labList = list.filter(sr => inferServiceRequestType(sr) === 'laboratory' || inferServiceRequestType(sr) === 'observation' || inferServiceRequestType(sr) === 'others');
    const imagingList = list.filter(sr => inferServiceRequestType(sr) === 'imaging');
    const procedureList = list.filter(sr => {
      const t = inferServiceRequestType(sr);
      return t === 'procedure' || t === 'surgery';
    });

    const labBatch$ = labList.length ? this.labBilling.batchResolveBillingAndPlaceholderForLab(labList) : of({} as any);
    const imagingBatch$ = imagingList.length ? this.labBilling.batchResolveBillingAndPlaceholderForImagingStudy(imagingList) : of({} as any);
    const procedureBatch$ = procedureList.length ? this.labBilling.batchResolveBillingAndPlaceholderForProcedure(procedureList) : of({} as any);

    return forkJoin({ chargeItems: chargeItems$, invoices: invoices$, labMap: labBatch$, imagingMap: imagingBatch$, procMap: procedureBatch$ }).pipe(
      map(({ chargeItems, invoices, labMap, imagingMap, procMap }) => {
        const allChargeItems = chargeItems || [];
        const allInvoices = invoices || [];

        const result: Record<string, { chargeItems: ChargeItem[]; invoices: Invoice[]; hasBalancedInvoice: boolean }> = {};

        for (const sr of list) {
          const id = sr.id!;
          // Prefer lab/imaging/procedure batch results when available
          if (labMap && labMap[id]) {
            result[id] = labMap[id];
            continue;
          }
          if (imagingMap && imagingMap[id]) {
            result[id] = imagingMap[id];
            continue;
          }
          if (procMap && procMap[id]) {
            result[id] = procMap[id];
            continue;
          }

          // Fallback: generic matching by ServiceRequest reference
          const srRef = `ServiceRequest/${id}`;

          const myChargeItems = allChargeItems.filter((ci) => {
            const services = Array.isArray(ci?.service) ? ci.service : [];
            return services.some((s: any) => {
              const ref = (s?.reference ?? '').toString();
              return ref === srRef || referenceMatches(ref, srRef);
            });
          });

          const chargeItemRefs = new Set(myChargeItems.filter((c: any) => !!c?.id).map((c: any) => `ChargeItem/${c.id}`));

          const myInvoices = allInvoices.filter((inv: any) => {
            const lineItems = inv?.lineItem || [];
            return lineItems.some((li: any) => {
              const ref = (li?.chargeItemReference?.reference || '').toString();
              if (!ref) return false;
              for (const target of chargeItemRefs) {
                if (ref === target || ref.endsWith(target)) return true;
              }
              return false;
            });
          }) as Invoice[];

          const hasBalancedInvoice = myInvoices.some((inv) => (inv?.status || '').toString().toLowerCase() === 'balanced');

          result[id] = {
            chargeItems: myChargeItems,
            invoices: myInvoices,
            hasBalancedInvoice
          };
        }

        return result;
      })
    );
  }
}
